// Jedyne miejsce, w którym front dotyka sieci.
//
// Sesja jedzie ciasteczkiem HttpOnly (panel nie ma jak go przeczytać z JS-a i
// dobrze) — stąd `credentials: 'same-origin'` i zero obsługi tokenów tutaj.

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** Kod maszynowy z odpowiedzi (np. "unverified"), jeśli serwer go podał. */
    readonly code: string | null = null,
  ) {
    super(message);
  }
}

async function req<T>(path: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: "same-origin",
    headers: opts.body ? { "content-type": "application/json" } : undefined,
    ...opts,
  });

  const text = await res.text();
  let data: unknown = null;
  if (text) {
    try {
      data = JSON.parse(text);
    } catch {
      // Panel zawsze odpowiada JSON-em. Nie-JSON znaczy, że odpowiedź nie
      // pochodzi z panelu (proxy, strona błędu nginxa) — mówimy to wprost,
      // zamiast pokazywać "Unexpected token < in JSON".
      throw new ApiError(`Nieoczekiwana odpowiedź serwera (HTTP ${res.status}).`, res.status);
    }
  }

  if (!res.ok) {
    const d = data as { error?: string; code?: string } | null;
    throw new ApiError(d?.error || `Błąd HTTP ${res.status}`, res.status, d?.code ?? null);
  }
  return data as T;
}

export const api = {
  get: <T>(p: string) => req<T>(p),
  post: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "POST", body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "PATCH", body: body === undefined ? undefined : JSON.stringify(body) }),
  put: <T>(p: string, body?: unknown) =>
    req<T>(p, { method: "PUT", body: body === undefined ? undefined : JSON.stringify(body) }),
  del: <T>(p: string) => req<T>(p, { method: "DELETE" }),
};

// --- Typy odpowiedzi panelu ---

export interface User {
  id: string;
  email: string;
  isAdmin: boolean;
  emailVerified: boolean;
}

export interface AuthConfig {
  /** off = brak; builtin = obrazek z kodem z /api/auth/captcha; turnstile = widżet Cloudflare. */
  captcha: "off" | "builtin" | "turnstile";
  captchaSiteKey: string | null;
  openRegistration: boolean;
  mailEnabled: boolean;
  /** Adres strony wizytówki — regulamin, polityka prywatności, bezpieczeństwo. */
  siteUrl: string | null;
}

export type Protocol = "ssh" | "ftp";
export type AuthKind = "password" | "key";

export interface Server {
  id: string;
  label: string;
  protocol: Protocol;
  host: string;
  port: number;
  username: string;
  secure: boolean;
  authKind: AuthKind;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface OptSpec {
  key: string;
  label: string;
  description: string;
  kind: "string" | "number" | "boolean" | "negate" | "enum" | "list" | "secret";
  choices?: string[];
  default?: string | number | boolean;
}

/** Lustrzane odbicie src/progress.ts — stan postępu skanu. */
export interface Progress {
  stage: "connect" | "walk" | "clamav" | "analyze" | "actions" | "health" | "system" | "report" | "done";
  label: string;
  done: number | null;
  total: number | null;
  files: number;
  candidates: number;
  threats: { backdoor: number; injected: number; suspicious: number; clean: number };
  withoutAi: number;
  actions: { quarantined: number; repaired: number; failed: number };
  costUsd: number;
  siteOk: boolean | null;
  at: string;
}

export type RunStatus = "running" | "done" | "failed" | "warning";

export interface RunUsage {
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  costUsd: number;
}

export interface RunSummary {
  runId: string;
  createdAt: string;
  finishedAt: string | null;
  status: RunStatus;
  mode: "report" | "quarantine";
  serverId: string | null;
  host: string | null;
  target: string | null;
  siteUrl: string | null;
  /** Czytelna nazwa tego, co run naprawia (domena albo katalog @ host). */
  domain: string;
  actions: number;
  quarantined: number;
  repaired: number;
  progress: Progress | null;
  threats: number | null;
  siteOk: boolean | null;
  usage: RunUsage | null;
  costUsd: number | null;
  pricePln: number | null;
  feedback: "ok" | "broken" | null;
  feedbackAt: string | null;
}

export type FeedbackVia = "none" | "assist" | "devops";

/** Wpis historii „czy strona działa?": zgłoszenie klienta, potwierdzenie albo odpowiedź admina. */
export interface RunFeedbackEntry {
  id: number;
  kind: "ok" | "problem" | "reply";
  message: string;
  via: FeedbackVia;
  author: string | null;
  createdAt: string;
}

export interface SupportRequest {
  id: string;
  userId: string;
  userEmail?: string;
  runId: string | null;
  runDomain: string | null;
  message: string;
  status: "open" | "in_progress" | "closed";
  hours: number;
  adminNote: string;
  createdAt: string;
  updatedAt: string;
  closedAt: string | null;
  invoice: { id: string; number: string; amountPln: number; status: InvoiceStatus } | null;
}

export type InvoiceStatus = "issued" | "paid" | "cancelled";

export interface Invoice {
  id: string;
  number: string;
  buyer: BillingProfile | null;
  userId: string;
  userEmail?: string;
  runId: string | null;
  runDomain: string | null;
  supportRequestId: string | null;
  kind: "repair" | "devops";
  amountPln: number;
  breakdown: Record<string, unknown>;
  note: string;
  status: InvoiceStatus;
  issuedAt: string;
  paidAt: string | null;
}

export interface BillingProfile {
  kind: "company" | "person";
  name: string;
  nip: string | null;
  address: string;
  postalCode: string;
  city: string;
  country: string;
  invoiceEmail: string | null;
  updatedAt?: string;
}

export const emptyBilling = (): BillingProfile => ({
  kind: "company", name: "", nip: "", address: "", postalCode: "", city: "", country: "PL", invoiceEmail: "",
});

export interface Pricing {
  baseFeePln: number;
  stepPln: number;
  aiMultiplier: number;
  usdPln: number;
  devopsHourlyPln: number;
  v2From: string;
  /** Słowny opis bieżącego algorytmu ceny naprawy. */
  description: string;
}

export interface AdminRun extends RunSummary {
  userEmail: string;
  invoice: { id: string; number: string; amountPln: number; status: InvoiceStatus } | null;
  assistCostUsd: number;
  price: { amountPln: number; algorithm: "v1" | "v2"; breakdown: Record<string, number | undefined> } | null;
  lastProblem: string | null;
  lastProblemVia: FeedbackVia | null;
  problemCount: number;
}

// --- Odwiedziny (admin) ---

export interface DayStats {
  day: string;
  loginPage: number;
  loginOk: number;
  loginFail: number;
  register: number;
  site: number;
  uniqueIps: number;
}

export interface AccessStats {
  days: DayStats[];
  totals: Omit<DayStats, "day">;
  recent: {
    id: number;
    event: "login_page" | "login_ok" | "login_fail" | "register" | "verify" | "site";
    userEmail: string | null;
    ip: string | null;
    userAgent: string | null;
    path: string | null;
    referrer: string | null;
    createdAt: string;
  }[];
  activeUsers: { email: string; lastSeenAt: string; sessions: number }[];
  topPaths: { path: string; views: number }[];
}

// --- Asystent AI ---

export type TranscriptPart =
  | { kind: "text"; text: string }
  | { kind: "tool"; toolUseId: string; name: string; summary: string; input: Record<string, unknown> }
  | { kind: "result"; toolUseId: string; text: string; isError: boolean };

export interface TranscriptItem {
  id: number;
  role: "user" | "assistant";
  author: string | null;
  createdAt: string;
  parts: TranscriptPart[];
}

export interface PendingAction {
  id: string;
  toolUseId: string;
  tool: "run_command" | "write_file";
  input: { command?: string; reason?: string; path?: string; content?: string };
  status: "pending" | "approved" | "rejected";
}

export interface AssistState {
  session: { id: string; runId: string; status: "open" | "closed"; usage: { calls: number; costUsd: number } };
  messages: TranscriptItem[];
  pendingActions: PendingAction[];
  busy: boolean;
}

export interface AdminUser {
  id: string;
  email: string;
  isAdmin: boolean;
  emailVerified: boolean;
  createdAt: string;
  runs: number;
  openSupport: number;
  hasBilling: boolean;
}

export interface ConnectionTest {
  ok: boolean;
  message: string;
  details: Record<string, string | number | boolean | null>;
}

export interface BrowseEntry {
  name: string;
  path: string;
  isDir: boolean;
  looksLikeWebRoot: boolean;
}

export interface BrowseResult {
  path: string;
  parent: string | null;
  entries: BrowseEntry[];
  looksLikeWebRoot: boolean;
  markers: string[];
}

export const pln = (n: number) =>
  `${n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
export const when = (iso: string) => new Date(iso).toLocaleString("pl");
